import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Dashboard from "./pages/Dashboard";
import GridDashboard from "./pages/GridDashboard";
import { LoginPage } from "./pages/LoginPage";
import { AdminPage } from "./pages/AdminPage";
import { msalConfig } from "./config/authConfig";
import { useState, useEffect } from "react";
import { Button } from "./components/ui/button";
import { X, Eye } from "lucide-react";

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promise BEFORE rendering
let msalInitialized = false;
const initPromise = msalInstance.initialize().then(() => {
  return msalInstance.handleRedirectPromise();
}).then((response) => {
  if (response) {
    msalInstance.setActiveAccount(response.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }
  
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
  
  msalInitialized = true;
}).catch((error) => {
  console.error("MSAL init error:", error);
  msalInitialized = true;
});

// Impersonation Banner Component
const ImpersonationBanner = ({ impersonatedUser, onExit }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between shadow-lg">
    <div className="flex items-center gap-2">
      <Eye className="w-4 h-4" />
      <span className="font-medium">
        Viewing as: {impersonatedUser?.name || impersonatedUser?.email}
      </span>
      <span className="text-amber-800 text-sm">
        ({impersonatedUser?.email})
      </span>
    </div>
    <Button
      variant="ghost"
      size="sm"
      onClick={onExit}
      className="text-amber-950 hover:bg-amber-600 hover:text-amber-950 gap-1"
    >
      <X className="w-4 h-4" />
      Exit Impersonation
    </Button>
  </div>
);

// Protected route wrapper
const ProtectedApp = () => {
  const { 
    isAuthenticated, 
    isAuthorized, 
    isAdmin, 
    isProjectManager, 
    user, 
    loading,
    isImpersonating,
    impersonatedUser,
    effectiveEmail,
    stopImpersonation,
  } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [viewingDashboardId, setViewingDashboardId] = useState(null);
  
  // Check for view parameter in URL or /it-dashboard route
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewId = params.get('view');
    if (viewId) {
      setViewingDashboardId(viewId);
    }
  }, []);
  
  // Check if user is requesting the IT Executive Dashboard via /it-dashboard route
  const isViewingITDashboard = window.location.pathname === '/it-dashboard';

  // Primary admin emails - get the original IT Executive Dashboard with full edit access
  const PRIMARY_ADMIN = "tplunkett@therapymgmt.com";
  const IT_DASHBOARD_OWNERS = [
    "tplunkett@therapymgmt.com",
    "cody.waldrop@therapymgmt.com",
  ];
  
  // Users who can view the IT Executive Dashboard (read-only view of admin's dashboard)
  // Note: cvictoriano is excluded here so they get their own GridDashboard with a link to admin's dashboard
  const IT_DASHBOARD_VIEWERS = [
    "tplunkett@therapymgmt.com",
    "cody.waldrop@therapymgmt.com",
    // "cvictoriano@therapymgmt.com", // Now uses GridDashboard with link to IT Dashboard
  ];

  // Don't show loading screen if we're already authenticated or impersonating
  // This prevents flickering during re-renders
  if (loading && !isAuthenticated && !isImpersonating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAuthorized) {
    return <LoginPage />;
  }

  // When impersonating, show GridDashboard for the impersonated user
  if (isImpersonating && impersonatedUser) {
    return (
      <>
        <ImpersonationBanner 
          impersonatedUser={impersonatedUser} 
          onExit={stopImpersonation} 
        />
        <div className="pt-10"> {/* Add padding for the banner */}
          <GridDashboard 
            onShowAdmin={(isAdmin || isProjectManager) ? () => setShowAdmin(true) : undefined}
            impersonatedEmail={effectiveEmail}
          />
        </div>
      </>
    );
  }

  if (showAdmin) {
    return (
      <AdminPage 
        onBack={() => setShowAdmin(false)} 
        onImpersonate={() => setShowAdmin(false)}
      />
    );
  }

  // Viewing another user's dashboard via link
  if (viewingDashboardId) {
    const label = sessionStorage.getItem('viewDashboardLabel') || 'Dashboard';
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-50 bg-blue-500 text-white px-4 py-2 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="font-medium">Viewing: {label}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setViewingDashboardId(null);
              sessionStorage.removeItem('viewDashboardId');
              sessionStorage.removeItem('viewDashboardLabel');
              window.history.pushState({}, '', '/');
            }}
            className="text-white hover:bg-blue-600 hover:text-white gap-1"
          >
            <X className="w-4 h-4" />
            Back to My Dashboard
          </Button>
        </div>
        <div className="pt-10">
          <GridDashboard 
            viewDashboardId={viewingDashboardId}
            isReadOnlyView={true}
          />
        </div>
      </>
    );
  }

  // Check if user can view IT Executive Dashboard
  const canViewITDashboard = IT_DASHBOARD_VIEWERS.some(
    email => email.toLowerCase() === user?.email?.toLowerCase()
  );
  
  // Handle /it-dashboard route - show IT Executive Dashboard in read-only mode
  if (isViewingITDashboard) {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-50 bg-primary text-primary-foreground px-4 py-2 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="font-medium">Viewing: IT Executive Dashboard</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              window.location.href = '/';
            }}
            className="text-primary-foreground hover:bg-primary/80 gap-1"
          >
            <X className="w-4 h-4" />
            Back to My Dashboard
          </Button>
        </div>
        <div className="pt-10">
          <Dashboard 
            isReadOnly={true}
            viewerEmail={user?.email}
          />
        </div>
      </>
    );
  }
  
  // Primary admin gets the original static IT Executive Dashboard (with all their data)
  // Authorized viewers also get to see the IT Executive Dashboard
  if (canViewITDashboard) {
    const isOwner = IT_DASHBOARD_OWNERS.some(
      email => email.toLowerCase() === user?.email?.toLowerCase()
    );
    return (
      <Dashboard 
        onShowAdmin={(isAdmin || isProjectManager) ? () => setShowAdmin(true) : undefined}
        isReadOnly={!isOwner}
        viewerEmail={user?.email}
      />
    );
  }

  // Other users get the customizable GridDashboard
  return <GridDashboard onShowAdmin={(isAdmin || isProjectManager) ? () => setShowAdmin(true) : undefined} />;
};

// Wrapper to wait for MSAL initialization
const AppWrapper = () => {
  const [ready, setReady] = useState(msalInitialized);

  useEffect(() => {
    if (!ready) {
      initPromise.then(() => setReady(true));
    }
  }, [ready]);

  if (!ready) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider>
        <AuthProvider>
          <div className="min-h-screen bg-background">
            <BrowserRouter>
              <Routes>
                <Route path="/*" element={<ProtectedApp />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </MsalProvider>
  );
};

function App() {
  return <AppWrapper />;
}

export default App;
